import {FlowTableLevel, FlowTableRow, MonthlyGraphData, ROW_TYPE} from '../model/flow-table-row.model';
import {createSelector} from '@ngrx/store';
import AccountingState from './accounting.reducer';
import {
  Account,
  AccountCategory,
  AccountNode,
  AccountType,
  BusinessYear,
  createRowNode,
  MonthlyAccounts,
  MonthlyCashFlow,
} from '../model/accounting.model';
import {State} from '../../core/reducers';
import {selectBusinessYears, selectYearMonthSpan,} from './span.selector';
import {selectMonthlyAccounts, selectMonthlyAccountsWithinExistingSpan,} from './monthly-accounts.selector';
import {fromBusinessYear} from '../model/period.model';
import {Dictionary} from '@ngrx/entity';
import {selectMonthlyCashFlowWithinExistingSpan} from './monthly-cashflow.selector';

export const getAccountingState = (state: State): AccountingState => state.accountingState;
export const selectCompanyInfo = createSelector(getAccountingState, (state: AccountingState) => state.companyInfo);
export const selectCompanyName = createSelector(selectCompanyInfo, (companyInfo) => companyInfo?.name);
export const selectAccounts = createSelector(getAccountingState, (state: AccountingState) => state.accounts);
export const selectAccountNumbers = createSelector(selectAccounts, (accounts: Account[]) => accounts.map(account => account.number));
export const selectAccountHierarchy = createSelector(getAccountingState, (state: AccountingState): AccountNode | null => state.accountHierarchy);
export const selectOpenedAccounts = createSelector(getAccountingState, (state: AccountingState): number[] => state.openedAccounts);

export const selectInitialTreasury = createSelector(
  selectMonthlyCashFlowWithinExistingSpan,
  (monthlyCashflows: MonthlyCashFlow[]): number | null => {
    return monthlyCashflows.length ? monthlyCashflows[0].treasury - monthlyCashflows[0].result : null;
  }
);

export const selectFinalTreasury = createSelector(
  selectMonthlyCashFlowWithinExistingSpan,
  (monthlyCashflows: MonthlyCashFlow[]): number | null => {
    return monthlyCashflows.length ? monthlyCashflows[monthlyCashflows.length - 1].treasury : null;
  }
);

export const selectCashFlowGraphData = createSelector(
  selectMonthlyCashFlowWithinExistingSpan,
  (monthlyCashflows: MonthlyCashFlow[]): MonthlyGraphData => {
    return monthlyCashflows.reduce((acc, monthlyCashFlow) => {
      return {
        input: [...acc.input, monthlyCashFlow.cashIn],
        output: [...acc.output, monthlyCashFlow.cashOut],
        result: [...acc.result, monthlyCashFlow.result],
        treasury: [...acc.treasury, monthlyCashFlow.treasury]
      };
    }, { input: [], output: [], result: [], treasury: [] } as MonthlyGraphData);
  }
);

function getRealTableData(yearMonthSpan: string[], rootNode: AccountNode | null, monthlyAccounts: MonthlyAccounts[], openedAccounts: number[]): FlowTableRow[] | undefined {
  if (rootNode && monthlyAccounts.length === yearMonthSpan.length) {
    const rootNodes = createRowNode(rootNode, 0, monthlyAccounts, openedAccounts).children;
    return [
      rootNodes[0], { ...rootNodes[0], name: 'result-operating', children: [], rowType: ROW_TYPE.recap },
    ];
  }
  return undefined;
}

export const selectRealTableData = createSelector(
  selectYearMonthSpan,
  selectAccountHierarchy,
  selectMonthlyAccountsWithinExistingSpan,
  selectOpenedAccounts,
  getRealTableData
);

export const selectRealTableDataWithMargin = createSelector(
  selectRealTableData,
  (rows?: FlowTableRow[]): FlowTableRow[] | undefined => {
    if (!rows) {
      return undefined;
    }
    const operatingRows = rows[0].children;
    const revenueRow = operatingRows.find(row => row.accountNo === 3);
    const costsGoodsAndServicesRow = operatingRows.find(row => row.accountNo === 4);
    const costsStaffRow = operatingRows.find(row => row.accountNo === 5);
    const costsOtherRow = operatingRows.find(row => row.accountNo === 6);
    const resultRow = rows[1];

    const firstGrossMarginValues = revenueRow?.values?.map((_, index) => revenueRow?.values[index] - (costsGoodsAndServicesRow?.values[index] ?? 0)) ?? [];
    const firstGrossMarginTotal = (revenueRow?.total ?? 0) - (costsGoodsAndServicesRow?.total ?? 0);

    const secondGrossMarginValues = firstGrossMarginValues.map((value, index) => value - (costsStaffRow?.values[index] ?? 0));
    const secondGrossMarginTotal = firstGrossMarginTotal - (costsStaffRow?.total ?? 0);

    const ebitdaValues = secondGrossMarginValues.map((value, index) => value - (costsOtherRow?.values[index] ?? 0));
    const ebitdaTotal = secondGrossMarginTotal - (costsOtherRow?.total ?? 0);

    const firstGrossMarginRow: FlowTableRow = {
      cat: AccountCategory.operating,
      type: AccountType.variation,
      name: 'gross-margin-1',
      accountNo: 0,
      level: FlowTableLevel.first,
      opened: false,
      color: null,
      values: firstGrossMarginValues,
      total: firstGrossMarginTotal,
      children: [],
      rowType: ROW_TYPE.intermediary,
      percent: false,
      depth: 1
    };
    const firstGrossMarginRowPercent: FlowTableRow = {
      cat: AccountCategory.operating,
      type: AccountType.variation,
      name: 'gross-margin-1',
      accountNo: 0,
      level: FlowTableLevel.first,
      opened: false,
      color: null,
      values: firstGrossMarginValues.map((value, index) => value / (revenueRow?.values[index] ?? 1)),
      total:firstGrossMarginTotal / (revenueRow?.total ?? 1),
      children: [],
      rowType: ROW_TYPE.intermediary,
      percent: true,
      depth: 1
    };
    const firstMarginCalculationIndex = operatingRows.findIndex(row => row.accountNo === 5);
    if (firstMarginCalculationIndex >= 0) {
      operatingRows.splice(firstMarginCalculationIndex, 0, firstGrossMarginRow, firstGrossMarginRowPercent);
    }

    const secondGrossMarginRow: FlowTableRow = {
      cat: AccountCategory.operating,
      type: AccountType.variation,
      name: 'gross-margin-2',
      accountNo: 0,
      level: FlowTableLevel.first,
      opened: false,
      color: null,
      values: secondGrossMarginValues,
      total: secondGrossMarginTotal,
      children: [],
      rowType: ROW_TYPE.intermediary,
      percent: false,
      depth: 1
    };
    const secondGrossMarginRowPercent: FlowTableRow = {
      cat: AccountCategory.operating,
      type: AccountType.variation,
      name: 'gross-margin-2',
      accountNo: 0,
      level: FlowTableLevel.first,
      opened: false,
      color: null,
      values: secondGrossMarginValues.map((value, index) => value / (revenueRow?.values[index] ?? 1)),
      total: secondGrossMarginTotal / (revenueRow?.total ?? 1),
      children: [],
      rowType: ROW_TYPE.intermediary,
      percent: true,
      depth: 1
    };
    const secondMarginCalculationIndex = operatingRows.findIndex(row => row.accountNo === 6);
    if (secondMarginCalculationIndex >= 0) {
      operatingRows.splice(secondMarginCalculationIndex, 0, secondGrossMarginRow, secondGrossMarginRowPercent);
    }

    const ebitdaRow: FlowTableRow = {
      cat: AccountCategory.operating,
      type: AccountType.variation,
      name: 'ebitda',
      accountNo: 0,
      level: FlowTableLevel.first,
      opened: false,
      color: null,
      values: ebitdaValues,
      total: ebitdaTotal,
      children: [],
      rowType: ROW_TYPE.intermediary,
      percent: false,
      depth: 1
    };
    const ebitdaRowPercent: FlowTableRow = {
      cat: AccountCategory.operating,
      type: AccountType.variation,
      name: 'ebitda',
      accountNo: 0,
      level: FlowTableLevel.first,
      opened: false,
      color: null,
      values: ebitdaValues.map((value, index) => value / (revenueRow?.values[index] ?? 1)),
      total: ebitdaTotal / (revenueRow?.total ?? 1),
      children: [],
      rowType: ROW_TYPE.intermediary,
      percent: true,
      depth: 1
    };
    const ebitdaCalculationIndex = operatingRows.findIndex(row => row.accountNo === 7);
    if (ebitdaCalculationIndex >= 0) {
      operatingRows.splice(ebitdaCalculationIndex, 0, ebitdaRow, ebitdaRowPercent);
    }

    const resultRowPercent: FlowTableRow = {
      cat: AccountCategory.operating,
      type: AccountType.variation,
      name: 'result-operating',
      accountNo: 0,
      level: FlowTableLevel.first,
      opened: false,
      color: null,
      values: resultRow.values.map((value, index) => value / (revenueRow?.values[index] ?? 1)),
      total: resultRow.total / (revenueRow?.total ?? 1),
      children: [],
      rowType: ROW_TYPE.recap,
      percent: true,
      depth: 1
    };
    rows[1].children.push(resultRowPercent)

    return rows;
  }
);

export const selectRealInputData = createSelector(
  selectRealTableData,
  (tableData: FlowTableRow[] | undefined): number[] => {
    if (tableData && tableData.length) {
      const length = tableData[0].values.length
      const inputRows = [
        tableData[0].children[0].values,
        // tableData[1].values // TODO properly handle these values
      ];
      return inputRows.reduce((cur, acc) => {
          return cur.map((value, index) => acc[index] + value)
        }
        , Array.from({ length }).fill(0) as number[]);
    }
    return [];
  }
);

const ACTIVITY_OPERATING_INDEX = 0;
// const INCOME_ACCOUNT_NUMBER = 3;
// const MATERIAL_EXPENSES_ACCOUNT_NUMBER = 4;
// const PERSONNEL_EXPENSES_ACCOUNT_NUMBER = 5;

export const selectRealOutputData = createSelector(
  selectRealTableData,
  (tableData: FlowTableRow[] | undefined): number[] => {
    if (tableData && tableData.length) {
      const spanLength = tableData[ACTIVITY_OPERATING_INDEX].values.length
      const inputRows: number[][] = [
        // TODO properly handle these values
        // tableData[ACTIVITY_OPERATING_INDEX].children.find(child => child.accountNo === INCOME_ACCOUNT_NUMBER)?.values ?? [],
        // tableData[ACTIVITY_OPERATING_INDEX].children.find(child => child.accountNo === MATERIAL_EXPENSES_ACCOUNT_NUMBER)?.values ?? [],
        // tableData[ACTIVITY_OPERATING_INDEX].children.find(child => child.accountNo === PERSONNEL_EXPENSES_ACCOUNT_NUMBER)?.values ?? [],
        tableData[ACTIVITY_OPERATING_INDEX].children.length > 1 ? tableData[ACTIVITY_OPERATING_INDEX].children[1].values : [],
        tableData[ACTIVITY_OPERATING_INDEX].children.length > 2 ? tableData[ACTIVITY_OPERATING_INDEX].children[2].values : [],
        tableData[ACTIVITY_OPERATING_INDEX].children.length > 3 ? tableData[ACTIVITY_OPERATING_INDEX].children[3].values : [],
        // TODO properly handle these values
        // tableData[ACTIVITY_OPERATING_INDEX].children[4].values,
        // tableData[ACTIVITY_OPERATING_INDEX].children[5].values,
        // tableData[2].values
      ];
      return inputRows.reduce((cur, acc) => {
          return cur.map((value, index) => acc[index] + value)
        }
        , Array.from({ length: spanLength }).fill(0) as number[]);
    }
    return [];
  }
);

export const selectRealTreasuryData = createSelector(
  selectRealTableData,
  (tableData: FlowTableRow[] | undefined): FlowTableRow | undefined => {
    if (tableData && tableData.length) {
      const flowTableRow = tableData[tableData.length - 1];
      return {...flowTableRow, values: flowTableRow.values.reduce((cur, acc, index) => {
        if (index === 0) {
          return [acc];
        }
        return [...cur, acc + cur[index - 1] ?? 0]
      }, [] as number[]) }
    }
    return undefined;
  }
);

export const selectRealGraphData = createSelector(
  selectRealInputData,
  selectRealOutputData,
  selectRealTreasuryData,
  (cashIn, cashOut, treasuryRows) => {
    const result = cashIn.map((value, index) => value - cashOut[index]);
    const treasury = treasuryRows?.values ?? [];
    return {
      input: cashIn,
      output: cashOut,
      result,
      treasury
    } as MonthlyGraphData;
  }
);

export const selectThreePreviousBusinessYears = createSelector(
  selectBusinessYears,
  (businessYears): BusinessYear[] => {
    if (businessYears.length < 2) {
      return [];
    }
    const businessYearsWithoutCurrentOne = businessYears.slice(0, businessYears.length - 1);
    while (businessYearsWithoutCurrentOne.length > 3) {
      businessYearsWithoutCurrentOne.shift();
    }
    return businessYearsWithoutCurrentOne;
  }
);

export const selectAverageResultFromLastThreePreviousYears = createSelector(
  selectAccountHierarchy,
  selectMonthlyAccounts,
  selectThreePreviousBusinessYears,
  (rootNode, allMonthlyAccounts: Dictionary<MonthlyAccounts>, businessYears): number => {
    const sum = businessYears.reduce((totalResult, businessYear) => {
      const yearMonthSpan = fromBusinessYear(businessYear);
      const monthlyAccountsWithinSpan = yearMonthSpan.reduce((accounts: MonthlyAccounts[], yearMonth) => {
        const monthlyAccounts = allMonthlyAccounts[yearMonth];
        if (monthlyAccounts) {
          const monthlyAccountsValuesToConsider = monthlyAccounts.accountsValues.filter(accountValue => Number(accountValue.accountNumber) < 6800);
          accounts.push({
            yearMonth: monthlyAccounts.yearMonth,
            accountsValues: monthlyAccountsValuesToConsider
          });
        }
        return accounts;
      }, []);
      if (rootNode && monthlyAccountsWithinSpan.length === yearMonthSpan.length) {
        const rootNodes = createRowNode(rootNode, 0, monthlyAccountsWithinSpan, []).children;
        return rootNodes[0].total + totalResult;
      } else {
        return totalResult;
      }
    }, 0);
    return sum / businessYears.length;
  }
);

export const selectValuationFromAverageResult = (capitalisationRate: number) => createSelector(
  selectAverageResultFromLastThreePreviousYears,
  (averageResult): number => averageResult / capitalisationRate
);
